import { useContext, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
    IRegisterPayload,
    loginRequest,
    registerRequest,
} from "../../shared/services/auth/auth.service";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AUTH_ROUTES } from "../../shared/routes/Constants";
import momAndChild from "../../../assets/images/parent-registration/momandchild.png";
import { toast } from "react-toastify";
import AuthContext from "../../shared/context/authContext/AuthContext";
import { ACTIONS } from "../../shared/context/authContext/Constants";
import { IUser } from "../../shared/interfaces/user.interface";
import styles from "./ParentRegister.module.scss";
import Turnstile from "../../shared/components/Turnstile";
import useCaptcha from "../../shared/hooks/useCaptcha";

declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
        fbq?: (...args: any[]) => void;
    }
}

const ParentRegister = () => {
    const { dispatch } = useContext(AuthContext);
    const history = useHistory();
    const location = useLocation();
    const captcha = useCaptcha();
    const [freeUser, setFreeUser] = useState(false);
    const [terms, setTerms] = useState(false);

    const [signUpSchema] = useState(
        Yup.object().shape({
            firstname: Yup.string().required("First name is required"),
            lastname: Yup.string().required("Last name is required"),
            email: Yup.string()
                .email("Invalid email")
                .required("Email is required."),
        })
    );

    const handleLogin = async (values: { email: string; password: string }) => {
        const { response, errors } = await loginRequest({
            ...values,
            email: values.email.toLowerCase(),
        });

        if (errors) {
            toast.error(
                "There was an error logging you in automatically, please try it yourself."
            );
        } else if (response) {
            const user: IUser = response.user;

            dispatch({
                type: ACTIONS.LOGIN,
                token: {
                    accessToken: response.accessToken,
                    refreshToken: response.refreshToken,
                },
            });

            dispatch({
                type: ACTIONS.SET_USER,
                user: response.user,
            });

            if (user && !user.firstLoginWeb) {
                localStorage.setItem("preview-video", "1");
            }

            history.push("/app/lets-play");
        }
    };

    useEffect(() => {
        if (location) {
            if (location.pathname === AUTH_ROUTES.FREE_REGISTER) {
                setFreeUser(true);
            } else {
                setFreeUser(false);
            }
        }
    }, [location]);

    /*
     * `history` added rather than silenced: `useHistory()` in react-router v5 returns a stable
     * reference, so this cannot change when the effect re-runs.
     */
    useEffect(() => {
        const userAgent = getMobileOperatingSystem();
        if (userAgent === "Android" || userAgent === "iOS") {
            history.push("/redirect-to-app");
        }
    }, [history]);

    const getMobileOperatingSystem = () => {
        const userAgent = navigator.userAgent;

        if (userAgent && /android/i.test(userAgent)) {
            return "Android";
        }

        if (userAgent && /iPad|iPhone|iPod/.test(userAgent)) {
            return "iOS";
        }

        return "unknown";
    };

    return (
        <div className="py-12 px-6">
            <img
                className={`mb-10 w-11/12 md-w-7/12 m-auto ${styles.banner}`}
                src={momAndChild}
                alt="parent registration banner"
            />
            <h1
                className={`text-aha-green font-century-gothic-bold text-3xl text-center mb-10 ${styles.title}`}
            >
                Help your child speak with confidence
            </h1>
            <div className={`m-auto ${styles.formContainer}`}>
                <Formik
                    initialValues={{
                        firstname: "",
                        lastname: "",
                        email: "",
                    }}
                    validationSchema={signUpSchema}
                    onSubmit={async (values, { setFieldError }) => {
                        if (!terms) {
                            toast.error(
                                "You must accept the terms and conditions."
                            );
                            return;
                        }

                        if (captcha.isMissing) {
                            toast.error("Please complete the verification.");
                            return;
                        }

                        const payload: IRegisterPayload = {
                            ...values,
                            email: values.email.toLowerCase(),
                            isB2cParent: true,
                            captchaToken: captcha.token ?? undefined,
                        };

                        if (freeUser) {
                            payload["freeUser"] = true;
                        }

                        const { message, password, errors } =
                            await registerRequest(payload);
                        captcha.reset();

                        if (errors) {
                            const errorMessage = errors
                                .map((x: any) => x.message)
                                .join("");

                            if (errorMessage === "Email already taken!") {
                                setFieldError(
                                    "email",
                                    "Email is already registered. Please sign in."
                                );
                            } else {
                                toast.error(errorMessage);
                            }
                        } else if (message) {
                            if (window.gtag) {
                                window.gtag("event", "conversion", {
                                    send_to:
                                        "AW-11035243742/18CGCLnR88MaEN7pgY4p",
                                });
                            }

                            if (
                                typeof window !== "undefined" &&
                                (window as any)?.fbq
                            ) {
                                window?.fbq?.("track", "StartTrial");
                            }

                            const values = {
                                email: payload.email,
                                password: password ?? "password",
                            };

                            handleLogin(values);
                        }
                    }}
                >
                    {({ errors, touched, isSubmitting }) => (
                        <Form className={"space-y-6"}>
                            <div>
                                <Field
                                    name="firstname"
                                    autoComplete={"new-password"}
                                    placeholder="FIRST NAME"
                                    className={
                                        "text-aha-black border-b-2 border-aha-orange focus:outline-none w-full p-2 placeholder-aha-green placeholder-opacity-50 bg-white tracking-widest font-century-gothic-bold"
                                    }
                                />
                                <ErrorMessage
                                    name="firstname"
                                    component={"div"}
                                    className="text-red-500 capitalize"
                                />
                            </div>
                            <div>
                                <Field
                                    name="lastname"
                                    autoComplete={"new-password"}
                                    placeholder="LAST NAME"
                                    className={
                                        "text-aha-black border-b-2 border-aha-orange focus:outline-none w-full p-2 placeholder-aha-green placeholder-opacity-50 bg-white tracking-widest font-century-gothic-bold"
                                    }
                                />
                                <ErrorMessage
                                    name="lastname"
                                    component={"div"}
                                    className="text-red-500 capitalize"
                                />
                            </div>
                            <div>
                                <Field
                                    name="email"
                                    autoComplete={"new-password"}
                                    placeholder="EMAIL"
                                    className={
                                        "text-aha-black border-b-2 border-aha-orange focus:outline-none w-full p-2 placeholder-aha-green placeholder-opacity-50 bg-white tracking-widest font-century-gothic-bold"
                                    }
                                />
                                <ErrorMessage
                                    name="email"
                                    component={"div"}
                                    className="text-red-500 capitalize"
                                />
                            </div>
                            <div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="terms"
                                        className="w-4 h-4"
                                        onChange={(e) =>
                                            setTerms(e.target.checked)
                                        }
                                    />
                                    <label
                                        htmlFor="horizontal-list-radio-license-3"
                                        className="w-full py-3 ml-2 text-xs text-aha-green"
                                    >
                                        I agree to the{" "}
                                        <Link
                                            className="underline text-blue-400"
                                            to={"/terms-and-conditions"}
                                        >
                                            terms/conditions
                                        </Link>{" "}
                                        and{" "}
                                        <Link
                                            className="underline text-blue-400"
                                            to={"/privacy-policy"}
                                        >
                                            privacy policy
                                        </Link>
                                        .
                                    </label>
                                </div>
                            </div>

                            <Turnstile
                                key={captcha.widgetKey}
                                onToken={captcha.setToken}
                            />

                            <div className={"text-center py-6"}>
                                <button
                                    disabled={isSubmitting}
                                    className={
                                        "border border-aha-black bg-aha-black text-white py-4 px-6 font-medium  hover:text-aha-black hover:bg-white transition duration-200 ease-in-out font-century-gothic-regular text-2xl tracking-wider"
                                    }
                                    type={"submit"}
                                >
                                    Start a 14-Day Free Trial
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default ParentRegister;
